/**
 * useGalleryEngine — the ONE place gallery behaviour lives.
 *
 * GAL-10. The 23 designs are pure presentation: they read state and
 * call actions off this engine and never touch Supabase, the proofing
 * hooks, or the download pipeline themselves. That is what lets
 * favorites, watermarks, ZIP downloads, comments and the PIN gate
 * behave identically in every design without being written 23 times,
 * and what lets each design be rendered in isolation with a mock
 * engine (see harness/) for pixel checks.
 *
 * It is the behaviour spec from the handoff's `gallery-core.js`
 * (PinGate · Gallery · slideshow · filters · toast), married to the
 * production wiring that already existed in PublicGallery.tsx:
 *   • picks / comments  → galleryProofing (server) or a local Set
 *   • downloads         → downloadZip + compositeWatermark + HEIC
 *   • watermark         → get_gallery_watermark (P0-1 rationale there)
 *   • video             → ready Mux files, gated on VIDEO_SHIPPED
 *   • PIN               → unlock_gallery_pin RPC, verified server-side
 *
 * GAL-13 retired the pre-design 'classic' gallery and its duplicate
 * download / proofing orchestration in PublicGallery; this engine is
 * now the only place that behaviour lives.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { saveBlob, saveUrl } from '@/lib/saveBlob';
import { toast as sonner } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { VIDEO_SHIPPED } from '@/config/launch';
import type { PublicGalleryPayload } from '@/services/galleries';
import {
  useMyPicks,
  useTogglePick,
  useGalleryComments,
  useAddComment,
  getPickerIdentifier,
  getPickerName,
  setPickerName,
  type GalleryComment,
} from '@/services/galleryProofing';
import {
  useGalleryVideoNotes,
  useGalleryVideoReviews,
  useAddVideoNote,
  useSetVideoNoteResolved,
  useSetVideoReview,
  type GalleryVideoNoteRow,
  type GalleryVideoReviewRow,
  type VideoReviewStatus,
} from '@/services/galleryReview';
import { isDemoId } from '@/lib/demoScope';
import { gridUrl, heroUrl, originalUrl } from '@/lib/imageTransform';
import { downloadZip, compositeWatermark, type ZipProgress } from '@/lib/downloadZip';
import type { WatermarkConfig } from '@/components/watermark/WatermarkOverlay';
import { useGalleryWatermark } from '@/services/watermark';
import { DEFAULT_TEMPLATE_ID, GALLERY_THEMES, backgroundsFor, groundFromHex, type GalleryTheme } from './themes';
import {
  parseCustomization,
  clampSlideshowSeconds,
  type GalleryCustomization,
  type GalleryTemplateId,
  HEX_COLOR,
  safeFontFamily,
  FONT_STYLES,
  isGalleryBackgroundId,
} from './types';

export type PublicPhoto = PublicGalleryPayload['photos'][number];
export type PublicVideo = PublicGalleryPayload['videos'][number];

/** One thing in the gallery — a photo or a film — in display order. */
export interface GalleryItem {
  id: string;
  kind: 'photo' | 'video';
  /** Position in the full (unfiltered) list, 0-based. */
  index: number;
  /** Grid-size URL (~800w transform for photos, Mux poster for film). */
  src: string;
  /** Lightbox-size URL (~2400w). */
  fullSrc: string;
  /** Original file URL (downloads). */
  originalSrc: string;
  /** Display caption — file name without extension, or the film name. */
  caption: string;
  /** width / height when known (GAL-7 dims, or 16:9 for film). */
  ratio: number | null;
  photo?: PublicPhoto;
  video?: PublicVideo;
  /** "2:14" for film. */
  durationLabel?: string;
}

export type GalleryFilter = 'all' | 'photos' | 'film' | 'favs';

export interface PinDot {
  filled: boolean;
}

export type { VideoReviewStatus };

/** A note pinned to a timecode on a film (designs 20–23). */
export interface VideoNote {
  id: string;
  /** `GalleryItem.id` of the film ("video-<uuid>"). */
  itemId: string;
  seconds: number;
  /** "02:14" */
  tc: string;
  body: string;
  /** Display name — "You" for the current viewer's own notes. */
  author: string;
  authorName: string | null;
  mine: boolean;
  resolved: boolean;
  /** Set on replies (the note they answer). */
  parentId: string | null;
  createdAt: string;
  /** "2h ago" */
  when: string;
}

/** The viewer's verdict on a film plus everyone's reactions. */
export interface VideoReview {
  status: VideoReviewStatus;
  /** The viewer marked it "select for final". */
  selected: boolean;
  /** Hearts from every viewer. */
  reactions: number;
}

/** One cut of a film — "Highlight film v3" / "v2" / "v1". */
export interface VideoVersion {
  item: GalleryItem;
  version: number;
  /** "v3" */
  label: string;
  /** The newest cut of the group. */
  current: boolean;
}

/** A downloadable rendition of a film. */
export interface VideoDownload {
  label: string;
  spec: string;
  url: string;
  /** The studio's highest-quality file (designs may hold it until the cut is approved). */
  master: boolean;
}

export interface GalleryEngine {
  // ── data ────────────────────────────────────────────────────────
  payload: PublicGalleryPayload;
  gallery: PublicGalleryPayload['gallery'];
  templateId: GalleryTemplateId;
  theme: GalleryTheme;
  customization: GalleryCustomization;
  studioName: string;
  logoUrl: string | null;
  /** Resolved accent: custom → brand → design. */
  accent: string;
  coverItem: GalleryItem | null;
  items: GalleryItem[];
  photoItems: GalleryItem[];
  filmItems: GalleryItem[];
  /** "12 photographs · 1 film" */
  countLabel: string;
  /** "June 2026" (gallery created_at) */
  dateLabel: string;
  isDemo: boolean;

  // ── access ──────────────────────────────────────────────────────
  locked: boolean;
  pinRequired: boolean;
  pin: string;
  pinError: boolean;
  pinBusy: boolean;
  pinDots: PinDot[];
  pressPin: (key: string) => void;

  // ── lightbox ────────────────────────────────────────────────────
  open: number | null;
  current: GalleryItem | null;
  /** "03 / 12" over the visible list. */
  counter: string;
  openItem: (id: string) => void;
  openAt: (visibleIndex: number) => void;
  close: () => void;
  step: (delta: number) => void;

  // ── favorites ───────────────────────────────────────────────────
  proofingEnabled: boolean;
  proofingLocked: boolean;
  pickLimit: number | null;
  limitReached: boolean;
  favIds: Set<string>;
  favItems: GalleryItem[];
  favCount: number;
  isFav: (id: string) => boolean;
  toggleFav: (id: string) => void;

  // ── filter ──────────────────────────────────────────────────────
  filter: GalleryFilter;
  setFilter: (f: GalleryFilter) => void;
  visibleItems: GalleryItem[];

  // ── slideshow ───────────────────────────────────────────────────
  slideshowEnabled: boolean;
  slideshow: boolean;
  slideshowSeconds: number;
  startSlideshow: () => void;
  stopSlideshow: () => void;
  toggleSlideshow: () => void;

  // ── actions ─────────────────────────────────────────────────────
  downloadEnabled: boolean;
  zipBuilding: boolean;
  zipProgress: ZipProgress;
  share: () => void;
  downloadAll: () => void;
  downloadItem: (item: GalleryItem) => void;
  downloadFavs: () => void;
  sendFavs: () => void;

  // ── comments (photos) ───────────────────────────────────────────
  commentsByPhotoId: Map<string, GalleryComment[]> | undefined;
  addComment: ((photoId: string, body: string) => Promise<void>) | undefined;

  // ── film playback ───────────────────────────────────────────────
  playingVideo: PublicVideo | null;
  playVideo: (item: GalleryItem) => void;
  stopVideo: () => void;

  // ── video review (designs 20–23) ────────────────────────────────
  /** Notes / verdicts reach the studio (proofing on, unlocked, not demo); otherwise they live on this device. */
  reviewShared: boolean;
  reviewerName: string | null;
  videoNotes: (item: GalleryItem) => VideoNote[];
  /** Unresolved notes across every film. */
  openNoteCount: number;
  addVideoNote: (
    item: GalleryItem,
    seconds: number,
    body: string,
    opts?: { parentId?: string | null; toast?: string },
  ) => void;
  setVideoNoteResolved: (note: VideoNote, resolved: boolean) => void;
  videoReview: (item: GalleryItem) => VideoReview;
  setVideoStatus: (item: GalleryItem, status: VideoReviewStatus, toast?: string) => void;
  toggleVideoSelected: (item: GalleryItem) => void;
  reactToVideo: (item: GalleryItem) => void;
  selectedFilms: GalleryItem[];
  approvedFilms: GalleryItem[];
  /** Confirms the "select for final" set (toast), like sendFavs. */
  sendSelectedFilms: () => void;
  /** Films that are the newest cut of their name group ("Highlight film v3" hides v2 / v1). */
  latestFilms: GalleryItem[];
  /** Every cut of a film's group, newest first (always includes the film itself). */
  videoVersions: (item: GalleryItem) => VideoVersion[];
  /** Real renditions: 1080p, plus the master when the plan stores one. */
  videoDownloads: (item: GalleryItem) => VideoDownload[];
  downloadVideo: (item: GalleryItem, download?: VideoDownload) => void;
  downloadFilms: (items: GalleryItem[]) => void;

  // ── toast (design pill, 2.2 s) ──────────────────────────────────
  toast: string | null;
  showToast: (message: string) => void;

  // ── watermark ───────────────────────────────────────────────────
  watermarkConfig: WatermarkConfig | null;

  // ── "Who's picking?" (rendered by the renderer, not the design) ─
  namePromptOpen: boolean;
  pendingName: string;
  setPendingName: (v: string) => void;
  submitName: () => void;
  cancelName: () => void;
}

const PIN_STORAGE_PREFIX = 'orbit-gallery-pin:';

const stripExt = (name: string) => name.replace(/\.[a-z0-9]{2,5}$/i, '');

const fmtDuration = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const total = Math.round(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return h > 0
    ? `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
    : `${m}:${s.toString().padStart(2, '0')}`;
};

/** "02:14" — the timecode format the film designs use. */
const tcLabel = (seconds: number): string => {
  const s = Math.max(0, Math.floor(Number.isFinite(seconds) ? seconds : 0));
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
};

/** "just now" · "5m ago" · "2h ago" · "3d ago" · "Jun 14". */
const relTime = (iso: string): string => {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const s = Math.max(0, (Date.now() - t) / 1000);
  if (s < 45) return 'just now';
  if (s < 3600) return `${Math.max(1, Math.round(s / 60))}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  if (s < 7 * 86400) return `${Math.round(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

/** "Highlight film v3" → { base: "highlight film", version: 3 }; no marker → v1. */
const VERSION_RE = /[\s_-]*\(?v(\d+)\)?\s*$/i;
const splitVersion = (caption: string): { base: string; version: number } => {
  const m = caption.match(VERSION_RE);
  if (!m || m.index === undefined) return { base: caption.trim().toLowerCase(), version: 1 };
  return { base: caption.slice(0, m.index).trim().toLowerCase(), version: Number(m[1]) };
};

/** Mux serves MP4 renditions as a download when asked to. */
const muxDownloadUrl = (url: string, filename: string): string =>
  `${url}${url.includes('?') ? '&' : '?'}download=${encodeURIComponent(filename)}`;

// DL-SAFARI-1 — one implementation, in saveBlob.
const clickDownload = (href: string, filename: string) => saveUrl(href, filename);

const EMPTY_NOTES: VideoNote[] = [];
const REVIEW_NONE: VideoReview = { status: 'review', selected: false, reactions: 0 };

const convertHeicToJpeg = async (blob: Blob): Promise<Blob> => {
  const heic2any = (await import('heic2any')).default;
  const converted = await heic2any({ blob, toType: 'image/jpeg', quality: 0.92 });
  return Array.isArray(converted) ? converted[0] : converted;
};

/**
 * Apply the studio's curated customization to a design's theme.
 *
 * SEC-XSS — this is the last gate before these values become CSS text.
 * Everything it returns is interpolated into template literals the
 * designs hand to `TemplateStyle`, i.e. into `dangerouslySetInnerHTML`.
 * Only `accent`, `display` and `body` are overridable; every other field
 * comes from the design constants in themes.ts and is never attacker
 * reachable. So these three lines are the whole user-controlled surface,
 * and each one is checked here rather than trusted from upstream.
 *
 * `brandColor` is why this function is checked and not just
 * `parseCustomization`. It never passes through that parser at all — it
 * arrives from `organizations.brand_color` via the public RPC's branding
 * payload and is assigned straight to `accent` when the studio ticks
 * "use my brand colour". `customization.accent` was hex-validated;
 * `brandColor` was the identical hole one line above it, and it is the
 * easier of the two to reach, because setting a brand colour is ordinary
 * studio setup rather than hand-crafted JSON.
 *
 * `rgba()` is no backstop either: it returns its input unchanged when the
 * hex won't parse, so a bad colour survives `rgba(accent, .5)` intact.
 */
export function resolveTheme(
  base: GalleryTheme,
  customization: GalleryCustomization,
  brandColor: string | null,
): GalleryTheme {
  let accent = base.accent;
  if (customization.useBrandColor && brandColor && HEX_COLOR.test(brandColor)) accent = brandColor;
  else if (customization.accent && HEX_COLOR.test(customization.accent)) accent = customization.accent;

  // Re-checked rather than trusted: not every customization reaching this
  // function came through parseCustomization (demo payloads and directly
  // constructed objects skip it), and an unrecognised face falling back
  // to the design's own is the correct outcome anyway.
  const displayFamily = safeFontFamily(customization.fonts?.display);
  const bodyFamily = safeFontFamily(customization.fonts?.body);

  // GALLERY-2 — an override asks Google Fonts only for the cuts that
  // face really has (FONT_STYLES): one weight or italic it lacks fails
  // the whole stylesheet, the design's own faces with it.
  const displayStyle = displayFamily ? FONT_STYLES[displayFamily] : undefined;
  const bodyStyle = bodyFamily ? FONT_STYLES[bodyFamily] : undefined;
  const display = displayFamily
    ? {
        family: displayFamily,
        fallback: base.display.fallback,
        weights: displayStyle?.weights ?? [400],
        italic: displayStyle?.italic ?? false,
      }
    : base.display;
  const body = bodyFamily
    ? {
        family: bodyFamily,
        fallback: base.body.fallback,
        weights: (bodyStyle?.weights ?? [400]).filter((w) => w <= 600),
      }
    : base.body;

  // GALLERY-2 — the studio's ground: the page and its ink (and, on
  // Basic, its greys and hairlines) from a fixed table, never from the
  // stored value itself — only the key is read. Accents stay the design's.
  let { bg, ink, muted, surface, isDark } = base;
  // GALLERY-ONE — a free colour wins over a keyed ground. Re-checked
  // here rather than trusted: demo payloads and directly constructed
  // objects skip parseCustomization, and this value IS interpolated
  // into CSS, which the keyed grounds never were.
  const custom = customization.backgroundHex;
  const ground = custom && HEX_COLOR.test(custom)
    ? groundFromHex(custom)
    : isGalleryBackgroundId(customization.background)
      ? backgroundsFor(base.id)[customization.background]
      : null;
  if (ground) {
    bg = ground.bg;
    ink = ground.ink;
    isDark = ground.isDark;
    if (ground.muted) muted = ground.muted;
    if (ground.line) surface = ground.line;
  }

  return { ...base, accent, display, body, bg, ink, muted, surface, isDark };
}

export interface GalleryEngineOptions {
  demoBadge?: boolean;
  /**
   * Starting review state for demo galleries and the harness — rows
   * shaped like the gallery_video_notes / gallery_video_reviews
   * tables (any author_identifier other than the viewer's reads as
   * another person). Ignored when review is shared with the studio.
   */
  seed?: { videoNotes?: GalleryVideoNoteRow[]; videoReviews?: GalleryVideoReviewRow[] };
}

export function useGalleryEngine(
  initialPayload: PublicGalleryPayload,
  opts: GalleryEngineOptions = {},
): GalleryEngine {
  // A successful PIN unlock swaps in the full payload; until then the
  // (possibly locked, media-less) payload from the read RPC is used.
  const [unlocked, setUnlocked] = useState<PublicGalleryPayload | null>(null);
  useEffect(() => setUnlocked(null), [initialPayload.gallery.id]);
  const payload = unlocked ?? initialPayload;

  const { gallery, photos, branding, videos } = payload;
  const isDemo = !!opts.demoBadge || isDemoId(gallery.id);
  const locked = !!payload.locked;
  const pinRequired = !!gallery.pin_required;

  const templateId: GalleryTemplateId =
    (gallery.template as GalleryTemplateId | undefined) ?? DEFAULT_TEMPLATE_ID;
  const customization = useMemo(
    () => parseCustomization(gallery.customization),
    [gallery.customization],
  );
  const brandColor = branding?.brand_color ?? null;
  const theme = useMemo(
    () => resolveTheme(GALLERY_THEMES[templateId] ?? GALLERY_THEMES[DEFAULT_TEMPLATE_ID], customization, brandColor),
    [templateId, customization, brandColor],
  );
  const studioName = branding?.studio_name ?? 'Studio';
  const logoUrl = branding?.logo_url ?? null;

  const proofingEnabled = !!gallery.proofing_enabled;
  const proofingLocked = !!gallery.proofing_locked;
  const pickLimit = gallery.proofing_pick_limit ?? null;
  const downloadEnabled = !!gallery.download_enabled;

  // ── Items ───────────────────────────────────────────────────────
  const readyVideos = useMemo(
    () => (VIDEO_SHIPPED ? videos.filter((v) => v.mux_playback_id) : []),
    [videos],
  );

  const items = useMemo<GalleryItem[]>(() => {
    const out: GalleryItem[] = photos.map((p, i) => ({
      id: p.id,
      kind: 'photo',
      index: i,
      src: gridUrl(p.file_url),
      fullSrc: heroUrl(p.file_url),
      originalSrc: originalUrl(p.file_url),
      caption: p.ai_scene || stripExt(p.name),
      ratio: p.width && p.height && p.height > 0 ? p.width / p.height : null,
      photo: p,
    }));
    readyVideos.forEach((v, j) => {
      out.push({
        id: `video-${v.id}`,
        kind: 'video',
        index: photos.length + j,
        src: v.poster_url ?? `https://image.mux.com/${v.mux_playback_id}/thumbnail.jpg?time=1&width=1200`,
        fullSrc: v.poster_url ?? `https://image.mux.com/${v.mux_playback_id}/thumbnail.jpg?time=1&width=2400`,
        originalSrc:
          v.mux_mp4_url ?? `https://stream.mux.com/${v.mux_playback_id}/capped-1080p.mp4`,
        caption: stripExt(v.file_name),
        ratio: 16 / 9,
        video: v,
        durationLabel:
          v.duration_seconds != null ? fmtDuration(Number(v.duration_seconds)) : undefined,
      });
    });
    return out;
  }, [photos, readyVideos]);

  const photoItems = useMemo(() => items.filter((i) => i.kind === 'photo'), [items]);
  const filmItems = useMemo(() => items.filter((i) => i.kind === 'video'), [items]);

  const coverItem = useMemo<GalleryItem | null>(() => {
    if (gallery.cover_photo_id) {
      const hit = photoItems.find((i) => i.id === gallery.cover_photo_id);
      if (hit) return hit;
    }
    return photoItems[0] ?? null;
  }, [gallery.cover_photo_id, photoItems]);

  const countLabel = useMemo(() => {
    const p = photoItems.length;
    const f = filmItems.length;
    const parts: string[] = [];
    if (p > 0) parts.push(`${p} photograph${p === 1 ? '' : 's'}`);
    if (f > 0) parts.push(`${f} film${f === 1 ? '' : 's'}`);
    return parts.join(' · ');
  }, [photoItems.length, filmItems.length]);

  const dateLabel = useMemo(() => {
    const d = new Date(gallery.created_at);
    return Number.isNaN(d.getTime())
      ? ''
      : d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  }, [gallery.created_at]);

  // ── Toast (the design's own pill, not sonner) ───────────────────
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<number | null>(null);
  const showToast = useCallback((message: string) => {
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    setToast(message);
    toastTimer.current = window.setTimeout(() => setToast(null), 2200);
  }, []);
  useEffect(
    () => () => {
      if (toastTimer.current) window.clearTimeout(toastTimer.current);
    },
    [],
  );

  // ── PIN gate ────────────────────────────────────────────────────
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState(false);
  const [pinBusy, setPinBusy] = useState(false);
  const pinResetTimer = useRef<number | null>(null);

  const tryUnlock = useCallback(
    async (code: string, opts2: { remember: boolean; silent?: boolean }) => {
      setPinBusy(true);
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data, error } = await (supabase as any).rpc('unlock_gallery_pin', {
          p_gallery_id: gallery.id,
          p_pin: code,
        });
        if (error) {
          if (!opts2.silent) {
            showToast(
              /too many/i.test(error.message ?? '')
                ? 'Too many tries — wait a minute'
                : "Couldn't check that code — try again",
            );
          }
          setPin('');
          return false;
        }
        if (!data) {
          if (!opts2.silent) {
            // Brief hold so the 4th dot is visible before the shake.
            pinResetTimer.current = window.setTimeout(() => {
              setPin('');
              setPinError(true);
            }, 250);
          } else {
            setPin('');
          }
          return false;
        }
        setUnlocked(data as PublicGalleryPayload);
        setPin('');
        setPinError(false);
        if (opts2.remember) {
          try {
            sessionStorage.setItem(PIN_STORAGE_PREFIX + gallery.id, code);
          } catch {
            /* private mode */
          }
        }
        return true;
      } finally {
        setPinBusy(false);
      }
    },
    [gallery.id, showToast],
  );

  // Silent re-unlock on refresh within the same tab.
  useEffect(() => {
    if (!locked || isDemo) return;
    let stored: string | null = null;
    try {
      stored = sessionStorage.getItem(PIN_STORAGE_PREFIX + gallery.id);
    } catch {
      /* ignore */
    }
    if (stored && /^\d{4}$/.test(stored)) void tryUnlock(stored, { remember: false, silent: true });
  }, [locked, isDemo, gallery.id, tryUnlock]);

  useEffect(
    () => () => {
      if (pinResetTimer.current) window.clearTimeout(pinResetTimer.current);
    },
    [],
  );

  const pressPin = useCallback(
    (key: string) => {
      if (!locked || pinBusy) return;
      if (key === 'del') {
        setPin((p) => p.slice(0, -1));
        setPinError(false);
        return;
      }
      if (!/^[0-9]$/.test(key)) return;
      setPinError(false);
      setPin((p) => {
        const next = (p + key).slice(0, 4);
        if (next.length === 4) void tryUnlock(next, { remember: true });
        return next;
      });
    },
    [locked, pinBusy, tryUnlock],
  );

  useEffect(() => {
    if (!locked) return;
    const h = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey) return;
      if (/^[0-9]$/.test(e.key)) {
        e.preventDefault();
        pressPin(e.key);
      } else if (e.key === 'Backspace') {
        e.preventDefault();
        pressPin('del');
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [locked, pressPin]);

  const pinDots = useMemo<PinDot[]>(
    () => [0, 1, 2, 3].map((i) => ({ filled: i < pin.length })),
    [pin.length],
  );

  // ── Favorites ───────────────────────────────────────────────────
  // Proofing on → persisted picks (studio sees them live).
  // Proofing off, or demo → a local Set so the design still works.
  const useServerPicks = proofingEnabled && !isDemo && !locked;
  const { data: myPicks = [] } = useMyPicks(useServerPicks ? gallery.id : undefined);
  const togglePick = useTogglePick(gallery.id);
  const [localFavs, setLocalFavs] = useState<Set<string>>(() => new Set());
  const serverFavs = useMemo(() => new Set(myPicks.map((p) => p.photo_id)), [myPicks]);
  const favIds = useServerPicks ? serverFavs : localFavs;
  const limitReached = pickLimit !== null && favIds.size >= pickLimit;

  // "Who's picking?" — a favorite, or any review action (`then`)
  // that needs a name before it can be shared with the studio.
  const [namePrompt, setNamePrompt] = useState<{ photoId: string } | { then: () => void } | null>(null);
  const [pendingName, setPendingName] = useState('');

  const commitFav = useCallback(
    (photoId: string, currentlyPicked: boolean) => {
      if (!useServerPicks) {
        setLocalFavs((prev) => {
          const next = new Set(prev);
          if (currentlyPicked) next.delete(photoId);
          else next.add(photoId);
          return next;
        });
        return;
      }
      togglePick.mutate(
        { photoId, currentlyPicked },
        {
          onError: (err) =>
            sonner.error(
              (err as Error)?.message || "That favorite didn't save — try tapping it again.",
            ),
        },
      );
    },
    [useServerPicks, togglePick],
  );

  const toggleFav = useCallback(
    (id: string) => {
      const item = items.find((i) => i.id === id);
      if (!item || item.kind !== 'photo') return;
      const currentlyPicked = favIds.has(id);
      if (useServerPicks) {
        if (proofingLocked) {
          showToast('Favorites are closed for this gallery');
          return;
        }
        if (!currentlyPicked && limitReached) {
          showToast(`You can pick up to ${pickLimit}`);
          return;
        }
        if (!currentlyPicked && !getPickerName()) {
          setNamePrompt({ photoId: id });
          return;
        }
      }
      commitFav(id, currentlyPicked);
    },
    [items, favIds, useServerPicks, proofingLocked, limitReached, pickLimit, commitFav, showToast],
  );

  const submitName = useCallback(() => {
    const name = pendingName.trim();
    if (name.length === 0) return;
    setPickerName(name);
    if (namePrompt && 'then' in namePrompt) namePrompt.then();
    else if (namePrompt && 'photoId' in namePrompt) {
      commitFav(namePrompt.photoId, favIds.has(namePrompt.photoId));
    }
    setNamePrompt(null);
    setPendingName('');
  }, [pendingName, namePrompt, commitFav, favIds]);

  const cancelName = useCallback(() => {
    setNamePrompt(null);
    setPendingName('');
  }, []);

  const favItems = useMemo(() => items.filter((i) => favIds.has(i.id)), [items, favIds]);
  const isFav = useCallback((id: string) => favIds.has(id), [favIds]);

  // ── Filter + lightbox ───────────────────────────────────────────
  const [filter, setFilterState] = useState<GalleryFilter>('all');
  const visibleItems = useMemo(() => {
    switch (filter) {
      case 'photos':
        return items.filter((i) => i.kind === 'photo');
      case 'film':
        return items.filter((i) => i.kind === 'video');
      case 'favs':
        return items.filter((i) => favIds.has(i.id));
      default:
        return items;
    }
  }, [items, filter, favIds]);

  const [open, setOpen] = useState<number | null>(null);
  const [slideshow, setSlideshow] = useState(false);
  const slideshowTimer = useRef<number | null>(null);

  const stopSlideshow = useCallback(() => {
    if (slideshowTimer.current) window.clearInterval(slideshowTimer.current);
    slideshowTimer.current = null;
    setSlideshow(false);
  }, []);

  const setFilter = useCallback(
    (f: GalleryFilter) => {
      stopSlideshow();
      setOpen(null);
      setFilterState(f);
    },
    [stopSlideshow],
  );

  const close = useCallback(() => {
    stopSlideshow();
    setOpen(null);
  }, [stopSlideshow]);

  const openAt = useCallback(
    (i: number) => {
      if (visibleItems.length === 0) return;
      setOpen(((i % visibleItems.length) + visibleItems.length) % visibleItems.length);
    },
    [visibleItems.length],
  );

  const openItem = useCallback(
    (id: string) => {
      const i = visibleItems.findIndex((x) => x.id === id);
      if (i >= 0) setOpen(i);
    },
    [visibleItems],
  );

  const step = useCallback(
    (d: number) => {
      setOpen((o) => {
        if (o === null || visibleItems.length === 0) return o;
        return (o + d + visibleItems.length) % visibleItems.length;
      });
    },
    [visibleItems.length],
  );

  const current = open === null ? null : (visibleItems[open] ?? null);
  const counter =
    open === null || visibleItems.length === 0
      ? ''
      : `${String(open + 1).padStart(2, '0')} / ${String(visibleItems.length).padStart(2, '0')}`;

  useEffect(() => {
    if (open === null) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowRight') step(1);
      if (e.key === 'ArrowLeft') step(-1);
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [open, close, step]);

  // If the visible list shrinks under the lightbox, close it.
  useEffect(() => {
    if (open !== null && open >= visibleItems.length) setOpen(null);
  }, [open, visibleItems.length]);

  // ── Slideshow ───────────────────────────────────────────────────
  const slideshowEnabled = customization.slideshow?.enabled ?? true;
  const slideshowSeconds = clampSlideshowSeconds(customization.slideshow?.seconds);

  const startSlideshow = useCallback(() => {
    if (visibleItems.length === 0) return;
    setOpen(0);
    setSlideshow(true);
    if (slideshowTimer.current) window.clearInterval(slideshowTimer.current);
    slideshowTimer.current = window.setInterval(() => step(1), slideshowSeconds * 1000);
  }, [visibleItems.length, slideshowSeconds, step]);

  const toggleSlideshow = useCallback(() => {
    if (slideshow) stopSlideshow();
    else startSlideshow();
  }, [slideshow, startSlideshow, stopSlideshow]);

  useEffect(
    () => () => {
      if (slideshowTimer.current) window.clearInterval(slideshowTimer.current);
    },
    [],
  );

  // ── Watermark (P0-1: resolved client-side via get_gallery_watermark) ─
  const { data: rpcWatermark = null } = useGalleryWatermark(isDemo || locked ? undefined : gallery.id);
  const rawWatermark = payload.watermark ?? rpcWatermark;
  const watermarkConfig: WatermarkConfig | null = useMemo(
    () =>
      rawWatermark
        ? {
            image_url: rawWatermark.image_url,
            text: rawWatermark.text,
            position: rawWatermark.position as WatermarkConfig['position'],
            opacity: rawWatermark.opacity,
            scale: rawWatermark.scale,
          }
        : null,
    [rawWatermark],
  );

  // ── Downloads ───────────────────────────────────────────────────
  const [zipBuilding, setZipBuilding] = useState(false);
  const [zipProgress, setZipProgress] = useState<ZipProgress>({
    current: 0,
    total: 0,
    phase: 'downloading',
  });

  const safeSlug =
    gallery.name.replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'gallery';

  const downloadItem = useCallback(
    async (item: GalleryItem) => {
      if (!downloadEnabled) return;
      if (item.kind === 'video') {
        const name = item.video?.file_name ?? 'film.mp4';
        clickDownload(muxDownloadUrl(item.originalSrc, name), name);
        showToast('Downloading film');
        return;
      }
      const photo = item.photo;
      if (!photo) return;
      showToast('Downloading full resolution');
      try {
        const response = await fetch(originalUrl(photo.file_url));
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        let blob = await response.blob();
        let outName = photo.name;
        if (/\.heic$/i.test(outName)) {
          try {
            blob = await convertHeicToJpeg(blob);
            outName = outName.replace(/\.heic$/i, '.jpg');
          } catch (error) {
            console.error('HEIC download conversion error:', error);
          }
        }
        if (watermarkConfig) {
          try {
            blob = await compositeWatermark(blob, watermarkConfig);
            outName = outName.replace(/\.(png|webp|heic)$/i, '.jpg');
            if (!/\.(jpe?g)$/i.test(outName)) outName = `${outName}.jpg`;
          } catch (err) {
            console.error('[watermark] single-photo bake failed:', err);
          }
        }
        // DL-SAFARI-1 — see saveBlob. Revoking in the click's own tick
        // made Safari save nothing, with no error anywhere.
        saveBlob(blob, outName);
      } catch (error) {
        console.error('Download error:', error);
        sonner.error(`Failed to download ${photo.name}`);
      }
    },
    [downloadEnabled, watermarkConfig, showToast],
  );

  const buildZip = useCallback(
    async (subset: PublicPhoto[], suffix: string) => {
      if (subset.length === 0 || zipBuilding) return;
      setZipBuilding(true);
      setZipProgress({ current: 0, total: subset.length, phase: 'downloading' });
      showToast(`Preparing ZIP · ${subset.length} photo${subset.length === 1 ? '' : 's'}`);
      try {
        const result = await downloadZip({
          photos: subset.map((p) => ({ id: p.id, file_url: p.file_url, name: p.name })),
          filename: `${safeSlug}-${suffix}.zip`,
          concurrency: 6,
          onProgress: setZipProgress,
          watermark: watermarkConfig,
        });
        if (result.failed > 0) {
          sonner.warning(
            `Downloaded ${result.succeeded} of ${subset.length}. ${result.failed} failed — try again for the rest.`,
          );
        } else {
          showToast(`Downloaded ${result.succeeded} photo${result.succeeded === 1 ? '' : 's'}`);
        }
        if (result.parts > 1) {
          sonner.info(
            `Your photos were split into ${result.parts} ZIP files to keep the download reliable — check your downloads folder for all ${result.parts} parts.`,
            { duration: 10000 },
          );
        }
        if (result.heicFallbacks > 0) {
          sonner.warning(
            `${result.heicFallbacks} iPhone photo${result.heicFallbacks === 1 ? '' : 's'} couldn't be converted and ${result.heicFallbacks === 1 ? 'was' : 'were'} included in the original HEIC format.`,
            { duration: 10000 },
          );
        }
      } catch (err) {
        console.error('[downloadZip] build failed:', err);
        sonner.error("Couldn't build the ZIP. Try again or download photos individually.");
      } finally {
        setZipBuilding(false);
      }
    },
    [zipBuilding, safeSlug, watermarkConfig, showToast],
  );

  const downloadAll = useCallback(() => {
    if (!downloadEnabled) return;
    void buildZip(photos, 'photos');
  }, [downloadEnabled, buildZip, photos]);

  const downloadFavs = useCallback(() => {
    if (!downloadEnabled) return;
    const subset = photos.filter((p) => favIds.has(p.id));
    if (subset.length === 0) {
      showToast('No favorites yet');
      return;
    }
    void buildZip(subset, 'favorites');
  }, [downloadEnabled, buildZip, photos, favIds, showToast]);

  const share = useCallback(() => {
    try {
      void navigator.clipboard.writeText(window.location.href);
    } catch {
      /* clipboard unavailable */
    }
    showToast('Gallery link copied');
  }, [showToast]);

  const sendFavs = useCallback(() => {
    const n = favIds.size;
    if (n === 0) {
      showToast('No favorites yet');
      return;
    }
    // Picks persist as you tap them, so "send" is a confirmation, not
    // a second write — and the honest copy says so.
    showToast(
      useServerPicks
        ? `${n} favorite${n === 1 ? '' : 's'} saved — ${studioName} can see them`
        : `${n} favorite${n === 1 ? '' : 's'} saved on this device`,
    );
  }, [favIds.size, useServerPicks, studioName, showToast]);

  // ── Comments ────────────────────────────────────────────────────
  const { data: allComments = [] } = useGalleryComments(!isDemo && !locked ? gallery.id : undefined);
  const addCommentMutation = useAddComment(gallery.id);

  const commentsByPhotoId = useMemo(() => {
    if (isDemo) return undefined;
    const map = new Map<string, GalleryComment[]>();
    for (const c of allComments as GalleryComment[]) {
      const arr = map.get(c.photo_id) ?? [];
      arr.push(c);
      map.set(c.photo_id, arr);
    }
    return map;
  }, [allComments, isDemo]);

  const addComment = useMemo(
    () =>
      isDemo
        ? undefined
        : (photoId: string, body: string) =>
            new Promise<void>((resolve, reject) => {
              addCommentMutation.mutate(
                { photoId, body },
                {
                  onSuccess: () => resolve(),
                  onError: (err) =>
                    reject(err instanceof Error ? err : new Error(String(err))),
                },
              );
            }),
    [isDemo, addCommentMutation],
  );

  // ── Film playback ───────────────────────────────────────────────
  const [playingVideo, setPlayingVideo] = useState<PublicVideo | null>(null);
  const playVideo = useCallback(
    (item: GalleryItem) => {
      if (item.kind !== 'video' || !item.video) return;
      stopSlideshow();
      setPlayingVideo(item.video);
    },
    [stopSlideshow],
  );
  const stopVideo = useCallback(() => setPlayingVideo(null), []);

  // ── Video review (GAL-12, designs 20–23) ────────────────────────
  // Same split as favorites: proofing on + unlocked + not demo →
  // shared with the studio through galleryReview; otherwise an
  // in-memory copy so the film designs work everywhere (harness,
  // demo galleries, proofing off).
  const reviewShared = proofingEnabled && !isDemo && !locked;
  const viewerId = useMemo(() => getPickerIdentifier(), []);
  const reviewerName = getPickerName();
  const { data: serverNotes = [] } = useGalleryVideoNotes(reviewShared ? gallery.id : undefined);
  const { data: serverReviews = [] } = useGalleryVideoReviews(reviewShared ? gallery.id : undefined);
  const addNoteMutation = useAddVideoNote(gallery.id);
  const resolveNoteMutation = useSetVideoNoteResolved(gallery.id);
  const setReviewMutation = useSetVideoReview(gallery.id);
  const [localNotes, setLocalNotes] = useState<GalleryVideoNoteRow[]>(() => opts.seed?.videoNotes ?? []);
  const [localReviews, setLocalReviews] = useState<GalleryVideoReviewRow[]>(() => opts.seed?.videoReviews ?? []);
  const noteRows = reviewShared ? (serverNotes as GalleryVideoNoteRow[]) : localNotes;
  const reviewRows = reviewShared ? (serverReviews as GalleryVideoReviewRow[]) : localReviews;

  const filmByVideoId = useMemo(() => {
    const m = new Map<string, GalleryItem>();
    for (const f of filmItems) if (f.video) m.set(f.video.id, f);
    return m;
  }, [filmItems]);

  const notesByItem = useMemo(() => {
    const map = new Map<string, VideoNote[]>();
    for (const r of noteRows) {
      const item = filmByVideoId.get(r.video_id);
      if (!item) continue;
      const mine = r.author_identifier === viewerId;
      const seconds = Number(r.timecode_seconds) || 0;
      const note: VideoNote = {
        id: r.id,
        itemId: item.id,
        seconds,
        tc: tcLabel(seconds),
        body: r.body,
        author: mine ? 'You' : r.author_name?.trim() || 'Guest',
        authorName: r.author_name,
        mine,
        resolved: r.resolved,
        parentId: r.parent_id,
        createdAt: r.created_at,
        when: relTime(r.created_at),
      };
      const arr = map.get(item.id) ?? [];
      arr.push(note);
      map.set(item.id, arr);
    }
    for (const arr of map.values()) {
      arr.sort((a, b) => a.seconds - b.seconds || a.createdAt.localeCompare(b.createdAt));
    }
    return map;
  }, [noteRows, filmByVideoId, viewerId]);

  const videoNotes = useCallback(
    (item: GalleryItem) => notesByItem.get(item.id) ?? EMPTY_NOTES,
    [notesByItem],
  );
  const openNoteCount = useMemo(() => {
    let n = 0;
    for (const arr of notesByItem.values()) for (const note of arr) if (!note.resolved) n += 1;
    return n;
  }, [notesByItem]);

  const reviewByItem = useMemo(() => {
    const map = new Map<string, VideoReview>();
    const mineSeen = new Set<string>();
    for (const r of reviewRows) {
      const item = filmByVideoId.get(r.video_id);
      if (!item) continue;
      const cur = map.get(item.id) ?? { ...REVIEW_NONE };
      cur.reactions += r.reactions;
      if (r.reviewer_identifier === viewerId) {
        cur.status = r.status;
        cur.selected = r.selected;
        mineSeen.add(item.id);
      } else if (!mineSeen.has(item.id)) {
        // Another reviewer's verdict shows until this viewer gives one;
        // "changes" outranks "approved".
        if (r.status === 'changes') cur.status = 'changes';
        else if (r.status === 'approved' && cur.status === 'review') cur.status = 'approved';
      }
      map.set(item.id, cur);
    }
    return map;
  }, [reviewRows, filmByVideoId, viewerId]);

  const videoReview = useCallback(
    (item: GalleryItem): VideoReview => reviewByItem.get(item.id) ?? REVIEW_NONE,
    [reviewByItem],
  );

  const reviewFailed = useCallback(
    (err: unknown) =>
      sonner.error((err as Error)?.message || "That didn't save — try again."),
    [],
  );

  const commitNote = useCallback(
    (item: GalleryItem, seconds: number, body: string, parentId: string | null, toastText: string) => {
      const videoId = item.video?.id;
      if (!videoId) return;
      const text = body.trim();
      if (!reviewShared) {
        const now = new Date().toISOString();
        setLocalNotes((prev) => [
          ...prev,
          {
            id: `local-${Date.now()}-${prev.length}`,
            gallery_id: gallery.id,
            video_id: videoId,
            author_identifier: viewerId,
            author_name: getPickerName(),
            timecode_seconds: seconds,
            body: text,
            resolved: false,
            parent_id: parentId,
            created_at: now,
            updated_at: now,
          },
        ]);
        showToast(toastText);
        return;
      }
      addNoteMutation.mutate(
        { videoId, seconds, body: text, parentId },
        { onSuccess: () => showToast(toastText), onError: reviewFailed },
      );
    },
    [reviewShared, gallery.id, viewerId, addNoteMutation, showToast, reviewFailed],
  );

  const addVideoNote = useCallback<GalleryEngine['addVideoNote']>(
    (item, seconds, body, opts2) => {
      if (item.kind !== 'video' || body.trim().length === 0) return;
      const parentId = opts2?.parentId ?? null;
      const toastText = opts2?.toast ?? `Note pinned at ${tcLabel(seconds)}`;
      if (reviewShared) {
        if (proofingLocked) {
          showToast('Notes are closed for this gallery');
          return;
        }
        if (!getPickerName()) {
          setNamePrompt({ then: () => commitNote(item, seconds, body, parentId, toastText) });
          return;
        }
      }
      commitNote(item, seconds, body, parentId, toastText);
    },
    [reviewShared, proofingLocked, commitNote, showToast],
  );

  const setVideoNoteResolved = useCallback(
    (note: VideoNote, resolved: boolean) => {
      if (!reviewShared) {
        setLocalNotes((prev) => prev.map((n) => (n.id === note.id ? { ...n, resolved } : n)));
        return;
      }
      if (proofingLocked) {
        showToast('Notes are closed for this gallery');
        return;
      }
      resolveNoteMutation.mutate({ noteId: note.id, resolved }, { onError: reviewFailed });
    },
    [reviewShared, proofingLocked, resolveNoteMutation, showToast, reviewFailed],
  );

  const commitReview = useCallback(
    (
      item: GalleryItem,
      patch: { status?: VideoReviewStatus; selected?: boolean; reactDelta?: number },
      toastText?: string,
    ) => {
      const videoId = item.video?.id;
      if (!videoId) return;
      if (!reviewShared) {
        const now = new Date().toISOString();
        setLocalReviews((prev) => {
          const i = prev.findIndex(
            (r) => r.video_id === videoId && r.reviewer_identifier === viewerId,
          );
          const base: GalleryVideoReviewRow =
            i >= 0
              ? prev[i]
              : {
                  id: `local-${videoId}`,
                  gallery_id: gallery.id,
                  video_id: videoId,
                  reviewer_identifier: viewerId,
                  reviewer_name: getPickerName(),
                  status: 'review',
                  selected: false,
                  reactions: 0,
                  created_at: now,
                  updated_at: now,
                };
          const next: GalleryVideoReviewRow = {
            ...base,
            status: patch.status ?? base.status,
            selected: patch.selected ?? base.selected,
            reactions: base.reactions + (patch.reactDelta ?? 0),
            updated_at: now,
          };
          return i >= 0 ? prev.map((r, j) => (j === i ? next : r)) : [...prev, next];
        });
        if (toastText) showToast(toastText);
        return;
      }
      setReviewMutation.mutate(
        { videoId, ...patch },
        {
          onSuccess: () => {
            if (toastText) showToast(toastText);
          },
          onError: reviewFailed,
        },
      );
    },
    [reviewShared, gallery.id, viewerId, setReviewMutation, showToast, reviewFailed],
  );

  /** Shared-mode gate for verdicts and selects: closed gallery → toast; no name → ask first. */
  const gatedReview = useCallback(
    (run: () => void, closedText: string) => {
      if (reviewShared) {
        if (proofingLocked) {
          showToast(closedText);
          return;
        }
        if (!getPickerName()) {
          setNamePrompt({ then: run });
          return;
        }
      }
      run();
    },
    [reviewShared, proofingLocked, showToast],
  );

  const setVideoStatus = useCallback(
    (item: GalleryItem, status: VideoReviewStatus, toastText?: string) => {
      if (item.kind !== 'video') return;
      gatedReview(() => commitReview(item, { status }, toastText), 'Review is closed for this gallery');
    },
    [gatedReview, commitReview],
  );

  const toggleVideoSelected = useCallback(
    (item: GalleryItem) => {
      if (item.kind !== 'video') return;
      const selected = !videoReview(item).selected;
      gatedReview(() => commitReview(item, { selected }), 'Selects are closed for this gallery');
    },
    [gatedReview, commitReview, videoReview],
  );

  const reactToVideo = useCallback(
    (item: GalleryItem) => {
      if (item.kind !== 'video') return;
      if (reviewShared && proofingLocked) return;
      commitReview(item, { reactDelta: 1 });
    },
    [reviewShared, proofingLocked, commitReview],
  );

  const selectedFilms = useMemo(
    () => filmItems.filter((f) => videoReview(f).selected),
    [filmItems, videoReview],
  );
  const approvedFilms = useMemo(
    () => filmItems.filter((f) => videoReview(f).status === 'approved'),
    [filmItems, videoReview],
  );

  const sendSelectedFilms = useCallback(() => {
    const n = selectedFilms.length;
    if (n === 0) {
      showToast('Select clips first');
      return;
    }
    showToast(
      reviewShared
        ? `${n} select${n === 1 ? '' : 's'} sent to ${studioName}`
        : `${n} select${n === 1 ? '' : 's'} saved on this device`,
    );
  }, [selectedFilms.length, reviewShared, studioName, showToast]);

  // Versions: films whose names differ only by a trailing v2 / v3 …
  // are cuts of one film; the newest is the one designs list.
  const versionsByItem = useMemo(() => {
    const groups = new Map<string, VideoVersion[]>();
    for (const item of filmItems) {
      const { base, version } = splitVersion(item.caption);
      const key = base || item.id;
      const arr = groups.get(key) ?? [];
      arr.push({ item, version, label: `v${version}`, current: false });
      groups.set(key, arr);
    }
    const byItem = new Map<string, VideoVersion[]>();
    for (const arr of groups.values()) {
      arr.sort((a, b) => b.version - a.version || b.item.index - a.item.index);
      arr[0].current = true;
      for (const v of arr) byItem.set(v.item.id, arr);
    }
    return byItem;
  }, [filmItems]);

  const latestFilms = useMemo(
    () => filmItems.filter((f) => versionsByItem.get(f.id)?.[0]?.item.id === f.id),
    [filmItems, versionsByItem],
  );

  const videoVersions = useCallback(
    (item: GalleryItem): VideoVersion[] =>
      versionsByItem.get(item.id) ?? [{ item, version: 1, label: 'v1', current: true }],
    [versionsByItem],
  );

  const videoDownloads = useCallback((item: GalleryItem): VideoDownload[] => {
    const v = item.video;
    if (!v) return [];
    const capped = `https://stream.mux.com/${v.mux_playback_id}/capped-1080p.mp4`;
    const out: VideoDownload[] = [{ label: '1080p', spec: 'H.264 · MP4', url: capped, master: false }];
    if (v.mux_mp4_url && v.mux_mp4_url !== capped) {
      out.unshift({ label: 'Master', spec: 'Highest quality · MP4', url: v.mux_mp4_url, master: true });
    }
    return out;
  }, []);

  const downloadVideo = useCallback(
    (item: GalleryItem, download?: VideoDownload) => {
      if (!downloadEnabled || item.kind !== 'video') return;
      const options = videoDownloads(item);
      const target = download ?? options[options.length - 1];
      if (!target) return;
      const base = stripExt(item.video?.file_name ?? item.caption);
      const name = `${base}${target.master ? '' : ` ${target.label}`}.mp4`;
      clickDownload(muxDownloadUrl(target.url, name), name);
      showToast(`Downloading ${item.caption} · ${target.label}`);
    },
    [downloadEnabled, videoDownloads, showToast],
  );

  const downloadFilms = useCallback(
    (list: GalleryItem[]) => {
      if (!downloadEnabled) return;
      const films = list.filter((i) => i.kind === 'video');
      if (films.length === 0) {
        showToast('Select clips first');
        return;
      }
      films.forEach((item, i) => {
        const name = item.video?.file_name ?? `${item.caption}.mp4`;
        window.setTimeout(() => clickDownload(muxDownloadUrl(item.originalSrc, name), name), i * 600);
      });
      showToast(`Downloading ${films.length} film${films.length === 1 ? '' : 's'}`);
    },
    [downloadEnabled, showToast],
  );

  return {
    payload,
    gallery,
    templateId,
    theme,
    customization,
    studioName,
    logoUrl,
    accent: theme.accent,
    coverItem,
    items,
    photoItems,
    filmItems,
    countLabel,
    dateLabel,
    isDemo,

    locked,
    pinRequired,
    pin,
    pinError,
    pinBusy,
    pinDots,
    pressPin,

    open,
    current,
    counter,
    openItem,
    openAt,
    close,
    step,

    proofingEnabled,
    proofingLocked,
    pickLimit,
    limitReached,
    favIds,
    favItems,
    favCount: favIds.size,
    isFav,
    toggleFav,

    filter,
    setFilter,
    visibleItems,

    slideshowEnabled,
    slideshow,
    slideshowSeconds,
    startSlideshow,
    stopSlideshow,
    toggleSlideshow,

    downloadEnabled,
    zipBuilding,
    zipProgress,
    share,
    downloadAll,
    downloadItem,
    downloadFavs,
    sendFavs,

    commentsByPhotoId,
    addComment,

    playingVideo,
    playVideo,
    stopVideo,

    reviewShared,
    reviewerName,
    videoNotes,
    openNoteCount,
    addVideoNote,
    setVideoNoteResolved,
    videoReview,
    setVideoStatus,
    toggleVideoSelected,
    reactToVideo,
    selectedFilms,
    approvedFilms,
    sendSelectedFilms,
    latestFilms,
    videoVersions,
    videoDownloads,
    downloadVideo,
    downloadFilms,

    toast,
    showToast,

    watermarkConfig,

    namePromptOpen: namePrompt !== null,
    pendingName,
    setPendingName,
    submitName,
    cancelName,
  };
}
